import { Temporal } from '@js-temporal/polyfill';

// The roster timeline's arithmetic, kept apart from the screen so it can be tested (CB-REQ CRW-12 at
// v1.960, CB-CONV §12 crew part 1 at v1.49). Instants in, percentages out: the bars are placed by
// where their instants fall in the span, so a day that loses or gains an hour to daylight saving is
// drawn as it is, not as 24 equal hours. The operational day starts at 05:00, as in the operator's
// earlier scheduler — a bump-out that runs past midnight stays on the night it belongs to.

export const DAY_STARTS_AT = 5;

export interface Span {
  readonly from: Temporal.Instant;
  readonly to: Temporal.Instant;
  /** The local date each operational day starts on. */
  readonly days: readonly string[];
}

/** `count` operational days from 05:00 on `date`, in `zone`. */
export function operationalSpan(date: string, count: number, zone: string): Span {
  const first = Temporal.PlainDate.from(date);
  const at = (d: Temporal.PlainDate) =>
    d.toZonedDateTime({ timeZone: zone, plainTime: { hour: DAY_STARTS_AT } }).toInstant();
  return {
    from: at(first),
    to: at(first.add({ days: count })),
    days: Array.from({ length: count }, (_, i) => first.add({ days: i }).toString()),
  };
}

/** Where an instant falls in the span, 0 – 100, clamped. */
export function percent(span: Span, instant: Temporal.Instant): number {
  const whole = span.to.epochMilliseconds - span.from.epochMilliseconds;
  const part = instant.epochMilliseconds - span.from.epochMilliseconds;
  return Math.min(100, Math.max(0, (part / whole) * 100));
}

/** A bar's left edge and width, in percent of the span. */
export function placement(
  span: Span,
  startsAt: string,
  endsAt: string,
): { left: number; width: number } {
  const left = percent(span, Temporal.Instant.from(startsAt));
  const right = percent(span, Temporal.Instant.from(endsAt));
  return { left, width: Math.max(right - left, 0.4) };
}

/** Each local midnight inside the span, with the date it begins. */
export function midnights(span: Span, zone: string): Array<{ date: string; at: number }> {
  return span.days.map((day) => {
    const next = Temporal.PlainDate.from(day).add({ days: 1 });
    const instant = next.toZonedDateTime({ timeZone: zone }).toInstant();
    return { date: next.toString(), at: percent(span, instant) };
  });
}

/** Hour ticks every `every` hours from the span's start, labelled on the local clock. */
export function ticks(
  span: Span,
  zone: string,
  every: number,
): Array<{ label: string; at: number }> {
  const out: Array<{ label: string; at: number }> = [];
  let t = span.from;
  while (Temporal.Instant.compare(t, span.to) < 0) {
    const local = t.toZonedDateTimeISO(zone);
    out.push({
      label: local.toPlainTime().toString({ smallestUnit: 'minute' }),
      at: percent(span, t),
    });
    t = t.add({ hours: every });
  }
  return out;
}

/**
 * Lanes for items that may overlap, first-fit by start: each item goes in the first lane whose last
 * item has ended. Two shifts of one person that clash end up in two lanes of that person's row, so
 * the clash is visible as a second bar rather than one bar hiding another.
 */
export function lanes<T extends { startsAt: string; endsAt: string }>(items: readonly T[]): T[][] {
  const sorted = [...items].sort((a, b) =>
    Temporal.Instant.compare(Temporal.Instant.from(a.startsAt), Temporal.Instant.from(b.startsAt)),
  );
  const out: T[][] = [];
  for (const item of sorted) {
    const start = Temporal.Instant.from(item.startsAt);
    const lane = out.find(
      (l) => Temporal.Instant.compare(Temporal.Instant.from(l[l.length - 1]!.endsAt), start) <= 0,
    );
    if (lane) lane.push(item);
    else out.push([item]);
  }
  return out;
}

/**
 * CB-CONV §12 crew part 4B: where "Open in roster" goes for a span of local wall-clock times — its
 * first operational day, and the fewest of 1, 3, 7 or 14 days that cover it, at most 14.
 */
export function rosterRange(
  localStart: string,
  localEnd: string,
): { from: string; days: 1 | 3 | 7 | 14 } {
  const day = (local: string) => {
    const at = Temporal.PlainDateTime.from(local);
    return (at.hour < DAY_STARTS_AT ? at.subtract({ days: 1 }) : at).toPlainDate();
  };
  const first = day(localStart);
  // An end exactly on 05:00 belongs to the day before it.
  const last = day(Temporal.PlainDateTime.from(localEnd).subtract({ minutes: 1 }).toString());
  const span = first.until(last).days + 1;
  const days = ([1, 3, 7, 14] as const).find((n) => n >= span) ?? 14;
  return { from: first.toString(), days };
}
